import React, { useState, useEffect } from "react";
import { useTranslations } from "use-intl";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { format } from "date-fns";
import { BankAccountsStatus, Receivable, TransactionSuggestion } from "@/types/payments";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { fetchWithPrecomputedFallback, settleSuggestionFetch } from "@/utils/matchSuggestionFallback";
import { useZeroMatchAcknowledgment } from "@/hooks/useZeroMatchAcknowledgment";
import { useMatchModalViewState, useMatchModalTitle } from "@/hooks/useMatchModalViewState";
import { useCorrectionBanner } from "@/i18n/useMatching";
import ExternalSettlementFields from "@/components/shared/ExternalSettlementFields";
import { parseActualSekAmount } from "@/components/shared/actualSekAmount";
import MatchModalBody from "@/components/shared/MatchModalBody";

interface MarkAsReceivedModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  receivable: Receivable | null;
  /**
   * See PayableMatchSuggestionsModal's `hasBankAccounts` docstring for the full contract.
   * `'loading'` / `'error'` (#498 whole-branch review finding I2) must never be treated as
   * `false` — that would silently route a receivable whose accounts are genuinely still
   * connected (or were, until a bank consent expired) through the trusted, unaudited case-a
   * write.
   */
  hasBankAccounts: BankAccountsStatus;
  onMatchTransaction: (receivableId: string, transactionId: string) => void;
  // actualSekAmount: FIN-1023 AC-3 — required by the backend for a non-SEK receivable
  // (case a only; acknowledgeExternalPayment/case c does not accept one, see its own
  // docstring below).
  onMarkReceivedExternally: (receivableId: string, formattedDate: string, actualSekAmount?: number) => void;
  /**
   * Case c (issue #498, Task 4) — a bank account is connected, but the search found no
   * candidates, or the consultant clicked "None of these match" on a non-empty result. Routes to
   * ReceivableService.acknowledgeExternalPayment (an audited write, distinct from
   * onMarkReceivedExternally's trusted case-a write with no audit row). Before this task, there
   * was no callback to reach for this state at all — the zero-candidate branch was a genuine dead
   * end (see the module-level comment below for how this was verified).
   */
  onAcknowledgeExternalPayment?: (receivableId: string, formattedDate: string) => void;
  formatCurrency: (amount: number, currency: string) => string;
}

/**
 * Issue #457: try the precomputed auto_match_decisions suggestion first (already computed by the
 * async match_jobs pipeline); fall back to the live JIT scoring query only when that's empty —
 * e.g. nothing has been scored yet for this receivable. `forceRefresh` (the explicit "Search
 * again" action) always calls the JIT path directly, regardless of what's precomputed, so a
 * transaction too new for the ~1-minute match-job cron to have scored yet can still be found.
 *
 * Issue #498 (Task 4) note: this precomputed+JIT fallback path is untouched — receivables (unlike
 * payables, which have no JIT path at all — see PayableMatchSuggestionsModal's docstring) always
 * had a real fallback query. What was missing was a UI path forward when BOTH the precomputed and
 * the JIT query genuinely returned zero candidates. That gap is what this task fixes below.
 */
function fetchSuggestions(
  receivableId: string,
  forceRefresh: boolean,
): Promise<TransactionSuggestion[]> {
  const service = ServiceRegistry.getReceivableService();
  return fetchWithPrecomputedFallback(
    () => service.getPrecomputedTransactionSuggestions(receivableId),
    () => service.getTransactionSuggestions(receivableId),
    forceRefresh,
  );
}

/**
 * Four states when `hasBankAccounts` is true (mirrors PayableMatchSuggestionsModal's now-
 * established pattern from issue #498 Task 3 — see that component's docstring for the fuller
 * rationale; the notes below focus on what's specific to receivables):
 *
 *  - still checking (fetch in flight) — a distinct loading state; the acknowledgment checkbox
 *    must never render before the fetch resolves (design spec AC7).
 *  - loadFailed — a distinct "couldn't check for matches, try again" retry state. Before this
 *    task, a rejected fetchSuggestions() call landed in the same `.catch` that also ran for a
 *    genuinely empty result (`setSuggestions([])` either way, no separate flag) — a failed search
 *    and a confirmed-empty one were visually and functionally identical. That conflation is the
 *    same bug class PayableMatchSuggestionsModal already fixed for issue #462's finding 3; this
 *    task applies the same fix here (design spec AC8, and `.claude/rules/client-error-handling.md`'s
 *    Tier 2 pattern, applied here even though this component lives in `src/`, not
 *    `apps/client/`).
 *  - candidates found — the existing suggestion table (case b, unchanged), plus a "None of these
 *    match" escape hatch (AC6) that transitions into the same acknowledgment flow used for zero
 *    candidates.
 *  - zero candidates (or "None of these match" was clicked) — plain-language text plus a single
 *    fixed-copy checkbox (ACKNOWLEDGMENT_COPY above) that must be ticked before the date-picker
 *    Confirm control is enabled (AC4/AC5). Before this task, this branch rendered "No matching
 *    transactions found." with no checkbox and no date-picker — the only interactive controls
 *    left in the modal were "Search again" (link) and a "Confirm Match" button that stayed
 *    permanently disabled because `selectedTransactionId` can never become truthy without a
 *    candidate row to click. That was a genuine, verified dead end: Cancel was the only way out.
 *    Unlike the payable version, this path does not include a foreign-currency settled-amount
 *    field — Task 2's backend `acknowledgeExternalPayment` action for receivables only accepts
 *    `receivableId` + `receivedDate` (see `supabase/functions/receivable-api/index.ts`), so there
 *    is nothing to capture here.
 */
const MarkAsReceivedModal: React.FC<MarkAsReceivedModalProps> = ({
  open,
  setOpen,
  receivable,
  hasBankAccounts,
  onMatchTransaction,
  onMarkReceivedExternally,
  onAcknowledgeExternalPayment,
  formatCurrency,
}) => {
  const t = useTranslations("consultantMatching.receivable");
  const correction = useTranslations("consultantMatching.correction");
  const buildCorrectionBanner = useCorrectionBanner("receivable");
  const getMatchModalTitle = useMatchModalTitle();
  // Boilerplate state every "mark as X" modal in this family opens with (selectedDate,
  // suggestions, loading, the selected-candidate id) — reported as a clone against receipts'
  // MarkAsMatchedModal.tsx, which still declares the same four hooks independently. Nothing to
  // extract: there's no shared logic behind these, just the same primitive useState shape.
  /* jscpd:ignore-start */
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [suggestions, setSuggestions] = useState<TransactionSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  /* jscpd:ignore-end */
  // Shared with PayableMatchSuggestionsModal (issue #498) — see the hook's own docstring.
  /* jscpd:ignore-start — identical hook-call destructure is the point: both callers share the
   * exact same useZeroMatchAcknowledgment contract by design (issue #498's "Consistency across
   * document types" requirement). Not further extractable without merging the two components. */
  const {
    loadFailed, setLoadFailed, noneOfTheseMatch, acknowledged, setAcknowledged,
    reset: resetAcknowledgment, onNoneOfTheseMatch: handleNoneOfTheseMatch, onBackToSuggestions: handleBackToSuggestions,
  } = useZeroMatchAcknowledgment(() => setSelectedTransactionId(null));
  /* jscpd:ignore-end */
  // FIN-1023 AC-3 — case a only (no bank account connected). No stored estimate is
  // prefilled here (unlike sales reports' actualSekAmountInput): a receivable's SEK
  // estimate isn't yet exposed on the frontend Receivable type, and prefilling isn't
  // part of AC-3 — the consultant always types the real amount received.
  const [actualSekAmountInput, setActualSekAmountInput] = useState("");

  // Pure async fetch — no synchronous setState in its own body, only inside
  // settleSuggestionFetch's .then/.catch/.finally callbacks, so calling it from a useEffect
  // never trips react-hooks/set-state-in-effect. The synchronous "reset before fetching"
  // setState calls live in the "adjust state during render" block below instead.
  const startSuggestionFetch = (receivableId: string, forceRefresh: boolean) => {
    settleSuggestionFetch(
      fetchSuggestions(receivableId, forceRefresh),
      { setSuggestions, setLoadFailed, setLoading },
      "Failed to fetch transaction suggestions:",
    );
  };

  const loadSuggestions = (receivableId: string, forceRefresh: boolean) => {
    setLoading(true);
    setSelectedTransactionId(null);
    resetAcknowledgment();
    startSuggestionFetch(receivableId, forceRefresh);
  };

  // "Adjust state during render" (React's own pattern, not a useEffect — avoids
  // react-hooks/set-state-in-effect; see FeedbackPanel.tsx for the established precedent in this
  // repo) resets the form each time the modal opens for a (possibly different) receivable,
  // closes, or `hasBankAccounts` resolves while the modal stays open for the same receivable
  // (#498 finding I2 — without tracking this third key too, a status transition from
  // 'loading'/'error' to `true` would skip resetting `loading`/`selectedTransactionId`, since
  // only the fetch-trigger effect below re-runs on that transition). prevOpen starts false so a
  // modal that mounts already open still runs the reset.
  const [prevOpen, setPrevOpen] = useState(false);
  const [prevReceivableId, setPrevReceivableId] = useState<string | null>(null);
  const [prevHasBankAccounts, setPrevHasBankAccounts] = useState<BankAccountsStatus>(hasBankAccounts);
  if (open !== prevOpen || (receivable?.id ?? null) !== prevReceivableId || hasBankAccounts !== prevHasBankAccounts) {
    setPrevOpen(open);
    setPrevReceivableId(receivable?.id ?? null);
    setPrevHasBankAccounts(hasBankAccounts);
    if (open && receivable) {
      // #498 Task 8 — reopening an already-resolved receivable shows the PREVIOUSLY entered
      // payment date instead of defaulting to today (design spec Flow 7 step 2). A first-time
      // mark-as-received has no date yet, so this falls through to `new Date()`, unchanged from
      // before this task.
      //
      // #498 whole-branch review finding I3b — prefilled from `manuallyHandledAt`, NOT
      // `paymentDate`. `paymentDate` is ALWAYS undefined for a receivable at runtime (it's
      // sourced from a `payments` row, a payable-only concept for real bank payment-initiation —
      // no `payments` row is ever created for a receivable). `manuallyHandledAt` is the field
      // BOTH `markAsReceivedExternally` (case a) and `acknowledgeExternalPayment` (case c)
      // actually write the consultant-chosen date to (`financial_documents.manually_handled_at`
      // via `ReceivableRepository.markAsManuallyHandled`) — see Receivable's docstring
      // (src/types/payments.ts) for the full trace of why `paymentDate` was always broken here.
      setSelectedDate(receivable.manuallyHandledAt ? new Date(receivable.manuallyHandledAt) : new Date());
      if (hasBankAccounts === true) {
        setLoading(true);
        setSelectedTransactionId(null);
        resetAcknowledgment();
      }
    }
    /* jscpd:ignore-start — the close-reset block and the fetch-trigger effect below are
     * identical in shape to payables' PayableMatchSuggestionsModal by design (same five-state
     * contract, issue #498); only the per-field state setters differ in name, not structure.
     * Not further extractable without merging the two components. */
    if (!open) {
      setSuggestions([]);
      setSelectedTransactionId(null);
      setSelectedDate(new Date());
      resetAcknowledgment();
      setActualSekAmountInput("");
    }
  }

  // The actual async side effect (fetching suggestions) stays a real useEffect — its body
  // calls only the pure-async startSuggestionFetch, never the reset-plus-fetch loadSuggestions.
  // #498 finding I2: only a resolved `true` triggers the search — 'loading'/'error' (bank
  // status unknown) must skip it, same as `false` (case a).
  useEffect(() => {
    if (open && receivable && hasBankAccounts === true) {
      startSuggestionFetch(receivable.id, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startSuggestionFetch is recreated every render, not a dependency
  }, [open, receivable, hasBankAccounts]);
  /* jscpd:ignore-end */

  const handleSearchAgain = () => {
    if (!receivable) return;
    loadSuggestions(receivable.id, true);
  };

  const hasSuggestions = suggestions.length > 0;
  // Shared with PayableMatchSuggestionsModal and receipts' MarkAsMatchedModal (issue #498) —
  // see the hook's own docstring for what each of the five states means. Called before the
  // `!receivable` early return below — React Hooks must run unconditionally on every render, and
  // `receivable` can transition from non-null to null while this modal stays open.
  const { showCandidateTable, showStillChecking, showLoadFailed, showBankStatusUnknown } =
    useMatchModalViewState({ hasBankAccounts, loading, loadFailed, hasSuggestions, noneOfTheseMatch });

  if (!receivable) return null;

  const handleConfirmMatch = () => {
    if (selectedTransactionId) {
      onMatchTransaction(receivable.id, selectedTransactionId);
      setOpen(false);
    }
  };

  const isForeignCurrency = receivable.currency !== "SEK";
  const { amount: parsedActualSekAmount } = parseActualSekAmount(actualSekAmountInput);
  const isActualSekAmountValid = parsedActualSekAmount !== null;

  // Case a: no bank accounts connected — trusted "received externally" write, unchanged path
  // for a SEK receivable (same 2-arg call as before this feature).
  const handleConfirmExternalPayment = () => {
    const formattedDate = format(selectedDate, "yyyy-MM-dd");
    if (isForeignCurrency) {
      onMarkReceivedExternally(receivable.id, formattedDate, parsedActualSekAmount);
    } else {
      onMarkReceivedExternally(receivable.id, formattedDate);
    }
    setOpen(false);
  };

  // Case c: zero-match / "None of these match" acknowledgment write (issue #498, Task 4). This is
  // the path that did not exist at all before this task — see the module-level comment for how
  // the dead end was verified.
  const handleConfirmAcknowledgment = () => {
    if (!acknowledged) return;
    if (onAcknowledgeExternalPayment) {
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      onAcknowledgeExternalPayment(receivable.id, formattedDate);
    }
    setOpen(false);
  };

  // #498 Task 8 — null whenever this is a first-time mark-as-received (receivable.status not yet
  // terminal); see buildCorrectionBanner's docstring.
  const correctionBanner =
    receivable.status === "paid" || receivable.status === "manually_handled"
      ? buildCorrectionBanner(receivable.paymentVerification)
      : null;

  const title = getMatchModalTitle(
    { showBankStatusUnknown, showLoadFailed, showCandidateTable, showStillChecking },
    hasBankAccounts,
    t("caseA"),
  );

  /* jscpd:ignore-start — the MatchModalBody call site (shell props, forwarded local state, and
   * the component's own closing boilerplate) is identical in shape to payables' and receipts'
   * call sites below the same props. Not further extractable without merging
   * payables/receivables/receipts into one generic component, which is a separate, larger,
   * higher-risk change (touches each page's own onPay/onMatchTransaction/
   * onMarkReceivedExternally call signatures) deliberately kept out of this PR's scope. */
  return (
    <MatchModalBody
      open={open}
      setOpen={setOpen}
      title={title}
      subtitle={<>{receivable.customer} — {formatCurrency(receivable.amount, receivable.currency)}</>}
      correctionBanner={correctionBanner && (
        <Alert>
          <AlertTitle>{correction("title")}</AlertTitle>
          <AlertDescription>{correctionBanner}</AlertDescription>
        </Alert>
      )}
      hasBankAccounts={hasBankAccounts}
      showBankStatusUnknown={showBankStatusUnknown}
      showStillChecking={showStillChecking}
      showLoadFailed={showLoadFailed}
      showCandidateTable={showCandidateTable}
      onRetry={handleSearchAgain}
      // Case a: no bank accounts connected — trusted date-picker, plus (FIN-1023 AC-3) a
      // required SEK-amount field for a non-SEK receivable: no bank transaction exists to
      // derive the real settled amount from, so the consultant must type it.
      caseAContent={
        <ExternalSettlementFields
          dateLabel={t("dateLabelA")}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
          showSekAmountField={isForeignCurrency}
          actualSekAmountInput={actualSekAmountInput}
          onActualSekAmountInputChange={setActualSekAmountInput}
          sekAmountFieldId="receivable-actual-sek-amount"
        />
      }
      onConfirmCaseA={handleConfirmExternalPayment}
      caseAConfirmLabel={t("confirm")}
      caseAConfirmDisabled={isForeignCurrency && !isActualSekAmountValid}
      suggestions={suggestions}
      selectedSuggestionId={selectedTransactionId}
      onSelectSuggestion={(suggestion) => setSelectedTransactionId(suggestion.transactionId)}
      formatCurrency={formatCurrency}
      onSearchAgain={handleSearchAgain}
      onNoneOfTheseMatch={handleNoneOfTheseMatch}
      onConfirmMatch={handleConfirmMatch}
      confirmMatchDisabled={!selectedTransactionId}
      acknowledgeCheckboxId="acknowledge-unmatched-receivable"
      acknowledged={acknowledged}
      onAcknowledgedChange={setAcknowledged}
      // Unlike the payable version, this path does not include a foreign-currency
      // settled-amount field — the backend acknowledgeExternalPayment action for receivables
      // only accepts receivableId + receivedDate (see
      // supabase/functions/receivable-api/index.ts), so there is nothing to capture.
      caseCContent={
        <ExternalSettlementFields
          dateLabel={t("dateLabelC")}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
          showSekAmountField={false}
          actualSekAmountInput={actualSekAmountInput}
          onActualSekAmountInputChange={setActualSekAmountInput}
          sekAmountFieldId="receivable-actual-sek-amount"
        />
      }
      noneOfTheseMatch={noneOfTheseMatch}
      onBackToSuggestions={handleBackToSuggestions}
      onConfirmAcknowledgment={handleConfirmAcknowledgment}
      confirmAcknowledgmentDisabled={!acknowledged}
    />
  );
};
/* jscpd:ignore-end */

export default MarkAsReceivedModal;
